import { State } from '@meraki-internal/state';
import dayjs from 'dayjs';
import { WithLinks } from '../caremap/WithLinks';
import { ICareMap } from '../caremap/ICareMap';
import { CaremapInsightsModelFactory } from './CaremapInsightsModelFactory';
import { IInsight, insightMediaType } from './IInsight';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { isExpectedFetchError } from '../support/isExpectedFetchError';
import { parseInsightRef } from './insightRef';

// How long an insight stays eligible for the unprompted surfaces. Sibling of the API's push-candidate
// age cap: once an observation is this old, presenting it as something Bea just noticed is misleading.
const PROACTIVE_MAX_AGE_DAYS = 30;

export class InsightsModel extends State<Record<string, never>> {
    constructor(
        private careMap: WithLinks<ICareMap>,
        private factory: CaremapInsightsModelFactory
    ) {
        super({});
    }

    private insights: IInsight[] = [];

    hasLoaded = false;

    onWebsocketMessage = async (message: IResourceChangedSocketMessage) => {
        if (message.resourceChangeType !== 'changed') {
            return;
        }

        const insightsHref = this.careMap.links.insights?.href;
        if (!insightsHref || message.resourceToRefresh.toLowerCase() !== insightsHref.toLowerCase()) {
            return;
        }

        try {
            await this.load();
        } catch (err: any) {
            // a transient/aborted fetch is expected here — it retries on the next trigger.
            // Alert only on genuine failures.
            if (!isExpectedFetchError(err)) {
                this.factory.logger.error(`InsightsModel.onWebsocketMessage failed reloading insights. Inner error: ${err.toString()}`);
            }
        }
    };

    load = async () => {
        const insightsLink = this.careMap.links.insights;

        // Viewers without get-private access aren't returned this link — treat as "no insights"
        // rather than throwing, so the UI can render an empty state instead of erroring.
        if (!insightsLink) {
            this.insights = [];
            this.hasLoaded = true;
            this.setState({});
            return;
        }

        this.insights = await this.factory.apiClient.get(insightsLink, insightMediaType);
        this.hasLoaded = true;
        this.setState({});
    };

    getInsights = (): IInsight[] => {
        return this.insights;
    };

    getActive = (): IInsight[] => {
        return this.insights.filter(insight => !insight.isDismissed);
    };

    // Insights we're allowed to push at the user unprompted (home section, note cards). Low-confidence
    // insights are stored but never proactive — they surface only when explicitly pulled, so getActive()
    // and getInsights() deliberately keep them for those pull surfaces. Stale ones drop out of the
    // unprompted surfaces too: an observation nobody has superseded in a month is no longer worth
    // presenting as current, and the Insights page still lists it.
    getActiveProactive = (): IInsight[] => {
        const freshCutoff = dayjs().subtract(PROACTIVE_MAX_AGE_DAYS, 'days');
        return this.getActive().filter(insight =>
            insight.confidence !== 'low' && dayjs(insight.createdAt).isAfter(freshCutoff)
        );
    };

    // Insights the current user has marked not useful (dismissed). The Insights page pulls these into
    // its "Not useful" archive so nothing is ever lost -- getActive() filters them out of the main list.
    getDismissed = (): IInsight[] => {
        return this.insights.filter(insight => insight.isDismissed);
    };

    getForNote = (noteId: string): IInsight[] => {
        return this.getActiveProactive().filter(insight =>
            insight.evidence.some(evidence => {
                const parsed = parseInsightRef(evidence.ref);
                return parsed && parsed.sourceId === noteId && parsed.contentType === 'note';
            })
        );
    };

    markSeen = async (insight: IInsight) => {
        const wasSeen = insight.isSeen;
        insight.isSeen = true;
        this.setState({});

        try {
            await this.factory.apiClient.put(insight.links.seen, {});
        } catch (err: any) {
            insight.isSeen = wasSeen;
            this.setState({});
            this.factory.logger.error(`InsightsModel.markSeen failed for insightId ${insight.insightId}. Inner error: ${err.toString()}`);
        }
    };

    dismiss = async (insight: IInsight, surface: string) => {
        const wasDismissed = insight.isDismissed;
        insight.isDismissed = true;
        this.setState({});

        if (!wasDismissed) {
            this.factory.tracking.track('Insight Dismissed', () => ({
                'Surface': surface,
                'Type': insight.type,
                'Confidence': insight.confidence
            }));
        }

        try {
            await this.factory.apiClient.put(insight.links.dismissed, {});
        } catch (err: any) {
            insight.isDismissed = wasDismissed;
            this.setState({});
            this.factory.logger.error(`InsightsModel.dismiss failed for insightId ${insight.insightId}. Inner error: ${err.toString()}`);
        }
    };

    sendFeedback = async (params: { insight: IInsight; helpful: boolean; surface: string; note?: string }) => {
        const { insight, helpful, surface, note } = params;
        const previousFeedback = insight.myFeedback;
        insight.myFeedback = { helpful, note, at: new Date().toISOString() };
        this.setState({});

        this.factory.tracking.track('Insight Feedback', () => ({
            'Helpful': helpful,
            // the stored note is a NOT_USEFUL_REASONS key; 'Reason' matches what the API's
            // 'Insight Vetted Out' calls the same idea, so the two read alike in a breakdown.
            // Omitted rather than sent as undefined when the user skipped the reason step.
            ...(note !== undefined && { 'Reason': note }),
            'Surface': surface,
            'Type': insight.type,
            'Confidence': insight.confidence
        }));

        try {
            await this.factory.apiClient.post(
                insight.links.feedback,
                { helpful, note },
                { accept: insightMediaType, 'content-type': insightMediaType }
            );
        } catch (err: any) {
            insight.myFeedback = previousFeedback;
            this.setState({});
            this.factory.logger.error(`InsightsModel.sendFeedback failed for insightId ${insight.insightId}. Inner error: ${err.toString()}`);
        }
    };
}
